import { z } from 'zod';
/** Global managed agent that owns every named-bot thread. */
export const formantBotAgentId = 'agt_formant_bot';
/** OpenAI realtime voices Understudy offers. Stored only; the thread page does not speak yet. */
export const formantBotVoices = [
    'marin',
    'cedar',
    'alloy',
    'ash',
    'ballad',
    'coral',
    'echo',
    'sage',
    'shimmer',
    'verse',
];
export const FormantBotVoiceSchema = z.enum(formantBotVoices);
/** Oscilloscope trace. Rendered from these numbers; nothing is stored as an image. */
export const FormantBotAvatarSchema = z
    .object({
    /** Hue in degrees, 0–360. */
    hue: z.number().min(0).max(360),
    /** How many wave cycles fit across the face. */
    cycles: z.number().min(0.5).max(8),
    /** Peak height as a fraction of the face, 0.15–0.95. */
    amplitude: z.number().min(0.15).max(0.95),
    /** Phase offset in radians. */
    phase: z
        .number()
        .min(0)
        .max(Math.PI * 2),
    /** Mix of a second, faster harmonic, 0–1. */
    harmonic: z.number().min(0).max(1),
    /** Glow strength, 0–1. */
    glow: z.number().min(0).max(1),
    /** One hue per trace. Each maps to a different Metaphysics accent. */
    lines: z.array(z.number().min(0).max(360)).min(2).max(4).optional(),
})
    .strict();
function hashSeed(seed) {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i++)
        h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
    return h >>> 0;
}
/** Stable trace for a bot that has not saved an avatar yet. */
export function formantBotAvatarFromSeed(seed) {
    const h = hashSeed(seed);
    let n = h || 1;
    const lines = pickDistinctHues(2, () => {
        n = Math.imul(n ^ (n >>> 13), 1274126177);
        return ((n >>> 0) % 10_000) / 10_000;
    });
    return {
        hue: lines[0] ?? h % 360,
        lines,
        cycles: 1 + (h % 40) / 16,
        amplitude: 0.35 + ((h >>> 8) % 40) / 100,
        phase: ((h >>> 16) % 628) / 100,
        harmonic: ((h >>> 4) % 100) / 100,
        glow: 0.35 + ((h >>> 12) % 50) / 100,
    };
}
/** Palette stops used by the console so a generation picks distinct colors. */
const avatarHueStops = [150, 190, 245, 28, 8, 100, 285];
function pickDistinctHues(count, rand = Math.random) {
    const bag = [...avatarHueStops];
    const hues = [];
    while (hues.length < count && bag.length > 0) {
        const i = Math.floor(rand() * bag.length);
        hues.push(bag.splice(i, 1)[0] ?? 150);
    }
    return hues;
}
/** A new random trace for the Generate button. Two differently colored lines. */
export function randomFormantBotAvatar() {
    const lines = pickDistinctHues(2);
    return {
        hue: lines[0] ?? 150,
        lines,
        cycles: 0.8 + Math.random() * 4.2,
        amplitude: 0.3 + Math.random() * 0.55,
        phase: Math.random() * Math.PI * 2,
        harmonic: Math.random(),
        glow: 0.25 + Math.random() * 0.75,
    };
}
export const FormantBotSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    instructions: z.string().nullable(),
    voice: FormantBotVoiceSchema.nullable(),
    avatar: FormantBotAvatarSchema.nullable(),
    persistentAgentId: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
const FormantBotScheduleInputSchema = z
    .object({
    cron: z.string().min(1).max(100),
    prompt: z.string().min(1).max(20_000),
    label: z.string().min(1).max(200).optional(),
})
    .strict();
export const CreateFormantBotInputSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    instructions: z.string().max(4000).nullable().optional(),
    voice: FormantBotVoiceSchema.nullable().optional(),
    avatar: FormantBotAvatarSchema.nullable().optional(),
    /** Heartbeats attached after the thread starts. */
    schedules: z.array(FormantBotScheduleInputSchema).max(5).optional(),
})
    .strict();
/** `null` clears a field. Omitted fields stay as they are. */
export const UpdateFormantBotInputSchema = z
    .object({
    name: z.string().trim().min(1).max(80).optional(),
    instructions: z.string().max(4000).nullable().optional(),
    voice: FormantBotVoiceSchema.nullable().optional(),
    avatar: FormantBotAvatarSchema.nullable().optional(),
})
    .strict();
/** Browser WebRTC offer. The API returns the realtime answer. */
export const OpenFormantBotVoiceInputSchema = z
    .object({
    sdp: z.string().min(1),
})
    .strict();
//# sourceMappingURL=formant-bot.js.map